import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Grid, Card, CardContent, Chip,
  IconButton, TextField, Tooltip, Stack,
  ToggleButton, ToggleButtonGroup, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, FormControlLabel, Checkbox
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  School as SchoolIcon, GridView as GridViewIcon, FormatListBulleted as TableListIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { academyStore } from '../services/academyStore';
import { CourseTemplate } from '../types/academy';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { useSystemParameters } from '../../Commun/contexts/SystemParametersContext';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import blobticService from '../../Commun/services/blobticService';

const ENTITY_KEY = 'Blobtic_Academy,academy_course';
const ACTION_SUFFIX = '';
const PRIMARY_KEY = 'CRS_IDF';

const fsx = {
  '& .MuiInputBase-root': { fontSize: '0.85rem' },
  '& .MuiInputLabel-root': { fontSize: '0.85rem' },
};

const dtoToCourse = (dto: any): CourseTemplate => ({
  id: dto.CRS_IDF,
  title: dto.CRS_TITLE || '',
  total_hours: Number(dto.CRS_TOTAL_HOURS) || 0,
  price: Number(dto.CRS_PRICE) || 0,
  description: dto.CRS_DESCRIPTION || '',
  start_date: dto.CRS_START_DATE
    ? new Date(dto.CRS_START_DATE).toISOString().split('T')[0]
    : undefined,
  end_date: dto.CRS_END_DATE
    ? new Date(dto.CRS_END_DATE).toISOString().split('T')[0]
    : undefined,
  show_price: dto.CRS_SHOW_PRICE == null ? true : Boolean(dto.CRS_SHOW_PRICE),
  age_band: dto.CRS_AGE_BAND || undefined,
  age_min: dto.CRS_AGE_MIN != null ? Number(dto.CRS_AGE_MIN) : undefined,
  age_max: dto.CRS_AGE_MAX != null ? Number(dto.CRS_AGE_MAX) : undefined,
  dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
  dos_code: dto.DOS_CODE || undefined,
  dos_lib: dto.DOS_LIB || undefined,
  user_lib: dto.USER_LIB || undefined,
  last_updt: dto.LAST_UPDT
    ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ')
    : undefined,
});

const courseToDto = (c: Partial<CourseTemplate>): Record<string, any> => ({
  CRS_IDF: (c as any).CRS_IDF ?? c.id,
  CRS_TITLE: c.title || '',
  CRS_TOTAL_HOURS: c.total_hours ?? 80,
  CRS_PRICE: c.price ?? 0,
  CRS_DESCRIPTION: c.description || '',
  CRS_START_DATE: c.start_date || null,
  CRS_END_DATE: c.end_date || null,
  CRS_SHOW_PRICE: c.show_price !== false,
  CRS_AGE_BAND: c.age_band || null,
  CRS_AGE_MIN: c.age_min ?? null,
  CRS_AGE_MAX: c.age_max ?? null,
  ...(c.dos_idf ? { DOS_IDF: c.dos_idf } : {}),
});

const emptyCourse = (): Partial<CourseTemplate> => ({
  title: '',
  total_hours: 80,
  price: 0,
  description: '',
  show_price: true,
});

export const CourseTemplatesPage: React.FC = () => {
  const { t } = useLanguage();
  
  const { getParam } = useSystemParameters();
  const currencySymbol = getParam('Currency_Symbol', 'DZD');

  const [view, setView] = useState<'list' | 'form'>('list');
  const [viewMode, setViewMode] = useDefaultViewMode();
  const [coursesList, setCoursesList] = useState<CourseTemplate[]>(() => academyStore.getCourses());
  const [listLoading, setListLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<CourseTemplate>>(emptyCourse());

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    content: string;
    color?: 'primary' | 'error';
    onConfirm: () => void;
  }>({ open: false, title: '', content: '', onConfirm: () => {} });

  useEffect(() => {
    academyStore.ensureCoursesLoaded();
    setCoursesList(academyStore.getCourses());
    return academyStore.subscribe(() => {
      setCoursesList(academyStore.getCourses());
    });
  }, []);

  const openNew = () => {
    setFormData(emptyCourse());
    setView('form');
  };

  const openEdit = (course: CourseTemplate) => {
    setFormData({ ...course });
    setView('form');
  };

  const backToList = () => {
    setView('list');
    setFormData(emptyCourse());
  };

  const set = <K extends keyof CourseTemplate>(key: K, value: CourseTemplate[K]) =>
    setFormData(prev => ({ ...prev, [key]: value }));
  const setField = set;

  const handleValidate = (): string | null => {
    if (!formData.title?.trim()) return t('academy.courses.val_title');
    if (formData.total_hours == null || formData.total_hours <= 0) return t('academy.courses.val_hours');
    if (formData.price == null || formData.price < 0) return t('academy.courses.val_price');
    if (formData.age_min != null && formData.age_max != null && formData.age_min > formData.age_max) {
      return t('academy.courses.val_age_range');
    }
    if (formData.start_date && formData.end_date && formData.start_date > formData.end_date) {
      return t('academy.courses.val_date_range');
    }
    return null;
  };

  // GenericPage auto-updates IndexedDB → academyStore refreshes via subscription.
  const handleDataChange = () => backToList();

  const handleClear = () => backToList();


  const handleDeleteFromList = (course: CourseTemplate) => {
    setConfirmDialog({
      open: true,
      title: t('common.confirm.title.delete') || 'Confirmer la suppression',
      content: t('academy.courses.confirm_delete', course.title),
      color: 'error',
      onConfirm: async () => {
        const res = await academyStore.deleteCourse(course.id);
        if (res.success) {
          blobticMessage.success(t('common.messages.success'), t('common.messages.deleted_success'));
        } else {
          blobticMessage.error(t('common.error'), res.message || 'Erreur lors de la suppression.');
        }
      },
    });
  };

  const truncateDesc = (desc: string, max = 120) =>
    desc.length > max ? `${desc.slice(0, max)}…` : desc;

  // ─── LIST VIEW ──────────────────────────────────────────────────────────────

  if (view === 'list') {
    return (
      <GenericPage
        title={t('academy.courses.title')}
        icon={<SchoolIcon />}
        footerEnabled={false}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onNew={openNew}
        newButtonLabel={t('academy.courses.add_new')}
      >

        {listLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">{t('common.loading') || 'Chargement...'}</Typography>
          </Box>
        ) : viewMode === 'grid' ? (
          <Grid container spacing={2.5}>
            {coursesList.map(course => (
              <Grid item xs={12} sm={6} md={4} key={course.id}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    transition: 'transform 0.2s',
                    '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ flex: 1, pr: 1 }}>
                        {course.title}
                      </Typography>
                      <SchoolIcon color="primary" />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                      <Chip
                        label={`${currencySymbol}${course.price}`}
                        color="success"
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                      <Chip
                        label={t('academy.courses.hours_label', course.total_hours)}
                        color="info"
                        size="small"
                      />
                      {course.age_band && (
                        <Chip label={course.age_band} size="small" variant="outlined" />
                      )}
                    </Box>

                    {course.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, minHeight: 40 }}>
                        {truncateDesc(course.description)}
                      </Typography>
                    )}
                    {(course.start_date || course.end_date) && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        {course.start_date || '—'} → {course.end_date || '—'}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <IconButton size="small" color="primary" onClick={() => openEdit(course)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteFromList(course)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <GenericTable
            columns={[
              { id: 'title', label: t('academy.courses.table.title'), minWidth: 200 },
              { id: 'total_hours', label: t('academy.courses.table.hours'), minWidth: 100 },
              {
                id: 'price',
                label: t('academy.courses.table.price'),
                minWidth: 120,
                format: (val: any) => `${currencySymbol}${val}`
              },
              { id: 'age_band', label: t('academy.courses.table.age_band'), minWidth: 120 },
              {
                id: 'start_date',
                label: t('academy.courses.table.validity'),
                minWidth: 180,
                format: (_val: any, row?: any) => {
                  const start = row?.start_date || '—';
                  const end = row?.end_date || '—';
                  return `${start} → ${end}`;
                }
              },
              {
                id: 'description',
                label: t('academy.courses.table.description'),
                minWidth: 250,
                format: (val: any) => truncateDesc(val || '', 80)
              },
            ]}
            data={coursesList}
            onEdit={(row) => openEdit(coursesList.find(c => c.id === row.id)!)}
            onDelete={(row) => handleDeleteFromList(coursesList.find(c => c.id === row.id)!)}
            searchPlaceholder={t('academy.courses.search_placeholder')}
            maxHeight="calc(100vh - 380px)"
          />
        )}

        <Dialog
          open={confirmDialog.open}
          onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>{confirmDialog.content}</DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))} variant="outlined" color="inherit">
              {t('btn.cancel')}
            </Button>
            <Button
              onClick={() => {
                const action = confirmDialog.onConfirm;
                setConfirmDialog(prev => ({ ...prev, open: false }));
                action();
              }}
              autoFocus
              color={confirmDialog.color || 'primary'}
              variant="contained"
            >
              {t('common.actions.confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      </GenericPage>
    );
  }

  // ─── FORM VIEW ──────────────────────────────────────────────────────────────

  const isEditing = !!(formData as any).CRS_IDF || !!formData.id;
  const formPayload = courseToDto(formData);

  return (
    <GenericPage
      title={
        isEditing
          ? t('academy.courses.form.title_edit', formData.title || '')
          : t('academy.courses.form.title_new')
      }
      icon={<SchoolIcon />}
      footerEnabled={true}
      footerInfo={{
        lastUpdate: formData.last_updt || '-',
        userLib: formData.user_lib || '-',
      }}
      additionalActions={
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={backToList}>
          {t('academy.courses.back_to_list')}
        </Button>
      }
      crudConfig={{
        entityKey: ENTITY_KEY,
        actionSuffix: ACTION_SUFFIX,
        primaryKey: PRIMARY_KEY,
      }}
      data={formPayload}
      onDataChange={() => handleDataChange()}

      onValidate={handleValidate}
      onClear={handleClear}
    >
      <Box sx={{ maxWidth: 800, mx: 'auto', pb: 4 }}>
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {t('academy.courses.section_info')}
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                label={t('academy.courses.form.title')}
                size="small"
                fullWidth
                required
                sx={fsx}
                value={formData.title ?? ''}
                onChange={e => setField('title', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('academy.courses.form.hours')}
                type="number"
                size="small"
                fullWidth
                required
                sx={fsx}
                value={formData.total_hours ?? 80}
                onChange={e => setField('total_hours', Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('academy.courses.form.price', currencySymbol)}
                type="number"
                size="small"
                fullWidth
                required
                sx={fsx}
                value={formData.price ?? 0}
                onChange={e => setField('price', Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.show_price !== false}
                    onChange={e => setField('show_price', e.target.checked)}
                  />
                }
                label={t('academy.courses.form.show_price')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('academy.courses.form.start_date')}
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={fsx}
                value={formData.start_date ?? ''}
                onChange={e => setField('start_date', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('academy.courses.form.end_date')}
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={fsx}
                value={formData.end_date ?? ''}
                onChange={e => setField('end_date', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('academy.courses.form.age_band')}
                size="small"
                fullWidth
                sx={fsx}
                value={formData.age_band ?? ''}
                onChange={e => setField('age_band', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('academy.courses.form.age_min')}
                type="number"
                size="small"
                fullWidth
                sx={fsx}
                value={formData.age_min ?? ''}
                onChange={e => setField('age_min', e.target.value ? Number(e.target.value) : undefined)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('academy.courses.form.age_max')}
                type="number"
                size="small"
                fullWidth
                sx={fsx}
                value={formData.age_max ?? ''}
                onChange={e => setField('age_max', e.target.value ? Number(e.target.value) : undefined)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('academy.courses.form.description')}
                size="small"
                fullWidth
                multiline
                rows={4}
                sx={fsx}
                value={formData.description ?? ''}
                onChange={e => setField('description', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>

        {(formData.dos_code || formData.dos_lib) && (
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
              {t('academy.courses.section_dossier')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('academy.courses.form.dossier_code')}
                  size="small"
                  fullWidth
                  disabled
                  sx={fsx}
                  value={formData.dos_code ?? ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('academy.courses.form.dossier_lib')}
                  size="small"
                  fullWidth
                  disabled
                  sx={fsx}
                  value={formData.dos_lib ?? ''}
                />
              </Grid>
            </Grid>
          </Paper>
        )}
      </Box>
    </GenericPage>
  );
};

export default CourseTemplatesPage;
